import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, KeyRound, Layers, MessageSquarePlus } from "lucide-react";
import { safeStorage } from "@/services/safeStorage";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { navForRole, ROLE_LABEL } from "@/components/roleNav";
import { managerService } from "@/services/paymentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { LocalPager, useLocalPage } from "@/components/LocalPager";
import { ClientStatusCard, ClientStatusHistory } from "@/components/ClientStatusCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const labelSmall = "block text-xs uppercase tracking-[0.2em] text-[#94A3B8]";
const card = "rounded-md border border-[#22252F] bg-[#12141A] p-4";
// O ID do cliente NÃO aparece na barra de endereço: entra por state/sessão e a rota é /clients.
const CLIENT_KEY = "pt_client_ctx";

export default function ClientProfilePage() {
  const { id: routeId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [id] = useState(() => routeId || state?.id || safeStorage.get(CLIENT_KEY) || "");
  const [profile, setProfile] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [password, setPassword] = useState("");
  const history = useLocalPage(profile?.login_history || [], 10);
  const [tier, setTier] = useState("padrao");
  const [extraLev, setExtraLev] = useState("");
  const [commentPage, setCommentPage] = useState(1);
  const [crmVersion, setCrmVersion] = useState(0);
  const perPage = 7;
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await managerService.profile(id);
      setProfile(data);
      setTier(data.account_tier || "padrao");
      setExtraLev(data.leverage_override ? String(data.leverage_override) : "");
      setComments(await managerService.comments(id));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) {
      navigate(user?.role === "admin" ? "/admin/users" : "/manager", { replace: true });
      return;
    }
    safeStorage.set(CLIENT_KEY, id);
    if (routeId) navigate("/clients", { replace: true, state: { id } });
  }, [id, routeId, navigate, user?.role]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("ID do cliente copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    try {
      await managerService.addComment(id, text.trim());
      setText("");
      toast.success("Comentário adicionado");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const submitTier = async (e) => {
    e.preventDefault();
    try {
      await managerService.setTier(id, tier, extraLev ? Number(extraLev) : null);
      toast.success("Categoria da conta atualizada");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const submitPassword = async (e) => {    e.preventDefault();
    try {
      await managerService.resetPassword(id, password);
      setPassword("");
      toast.success("Palavra-passe alterada");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const stats = [
    { id: "balance", label: "Saldo", value: fmtMoney(profile?.virtual_balance || 0) },
    { id: "deposits", label: "Depósitos aprovados", value: fmtMoney(profile?.deposits_total || 0) },
    { id: "withdrawals", label: "Retiradas aprovadas", value: fmtMoney(profile?.withdrawals_total || 0) },
    { id: "pnl", label: "P/L realizado", value: fmtMoney(profile?.realized_pnl || 0) },
    { id: "positions", label: "Posições abertas", value: profile?.open_positions ?? 0 },
    { id: "trades", label: "Operações", value: profile?.trades ?? 0 },
  ];

  return (
    <AppShell nav={navForRole(user?.role)}
              title={profile?.name || "Perfil do cliente"}
              subtitle={`Ficha interna do cliente · sessão: ${ROLE_LABEL[user?.role] || "—"}`}>
      <div data-testid="client-profile-page" className="space-y-6">
        <button data-testid="back-to-clients" onClick={() => navigate(user?.role === "admin" ? "/admin/users" : "/manager")}
                className="flex items-center gap-2 text-sm text-[#94A3B8] transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Voltar aos clientes
        </button>

        <div className={card}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { id: "email", label: "Email", value: profile?.email },
              { id: "ip", label: "Último IP", value: profile?.last_ip || "—" },
              { id: "login", label: "Último acesso", value: fmtDate(profile?.last_login_at) },
              { id: "manager", label: "Gestor responsável", value: profile?.manager_name || "—" },
              { id: "agent", label: "Agente responsável", value: profile?.agent_name || "—" },
              { id: "client-id", label: "ID do cliente", value: profile?.id || "—", copy: true },
              { id: "phone", label: "Telefone", value: profile?.phone ? `${profile.phone_dial || ""} ${profile.phone}` : "—" },
              { id: "tier", label: "Categoria da conta", value: `${profile?.account_tier_label || "Padrão"} · 1:${profile?.leverage || 30}` },
            ].map((f) => (
              <div key={f.id} data-testid={`profile-${f.id}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{f.label}</p>
                <p className="mt-1 flex items-center gap-2 break-all font-mono text-sm text-white">
                  {f.value}
                  {f.copy && profile?.id ? (
                    <button onClick={copyId} data-testid="copy-client-id" title="Copiar ID"
                            className="text-[#94A3B8] transition-colors hover:text-white">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {stats.map((s) => (
            <div key={s.id} data-testid={`profile-stat-${s.id}`} className={card}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{s.label}</p>
              <p className="mt-2 font-mono text-base text-white">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <ClientStatusCard clientId={id} profile={profile}
                              onChanged={() => { load(); setCrmVersion((v) => v + 1); }} />

            <form onSubmit={submitComment} className={`${card} space-y-3`} data-testid="comment-form">
              <p className="flex items-center gap-2 text-sm text-white"><MessageSquarePlus className="h-4 w-4" /> Novo comentário interno</p>
              <Textarea required minLength={2} data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)}
                        placeholder="Notas visíveis apenas para gestores e administradores"
                        className="min-h-[90px] rounded-md border-[#22252F] bg-[#0F1116] text-white" />
              <Button type="submit" data-testid="submit-comment"
                      className="rounded-md bg-[#10B981] text-xs uppercase tracking-[0.15em] text-[#04150F] hover:bg-[#0EA271]">
                Adicionar
              </Button>
            </form>

            <div className={`${card} max-h-[520px] overflow-y-auto`} data-testid="comments-list">
              {comments.slice((commentPage - 1) * perPage, commentPage * perPage).map((c) => (
                <div key={c.id} className="border-b border-[#1A1D25] py-3 last:border-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#94A3B8]">
                    <span className="text-white">{c.author_name}</span>
                    <span className="font-mono uppercase text-[#60A5FA]">{c.author_role}</span>
                    <span className="font-mono">{fmtDate(c.created_at)}</span>
                  </div>
                  <p className={`mt-1 text-sm ${c.system ? "italic text-[#94A3B8]" : "text-white"}`}>{c.text}</p>
                </div>
              ))}
              {comments.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#64748B]">Ainda sem comentários</p>
              ) : null}
              {comments.length > perPage ? (
                <div className="mt-3 flex items-center justify-between text-xs text-[#94A3B8]">
                  <button data-testid="comments-prev" disabled={commentPage <= 1}
                          onClick={() => setCommentPage(commentPage - 1)}
                          className="rounded-md border border-[#22252F] px-3 py-2 disabled:opacity-40">Anterior</button>
                  <span data-testid="comments-page-label" className="font-mono">
                    Página {commentPage} / {Math.ceil(comments.length / perPage)}
                  </span>
                  <button data-testid="comments-next"
                          disabled={commentPage >= Math.ceil(comments.length / perPage)}
                          onClick={() => setCommentPage(commentPage + 1)}
                          className="rounded-md border border-[#22252F] px-3 py-2 disabled:opacity-40">Seguinte</button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            {["admin", "manager"].includes(user?.role) ? (
              <form onSubmit={submitTier} className={`${card} space-y-3`} data-testid="tier-form">
                <p className="flex items-center gap-2 text-sm text-white"><Layers className="h-4 w-4" /> Categoria da conta</p>
                <select data-testid="tier-select" value={tier} onChange={(e) => setTier(e.target.value)}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  {(profile?.tiers || []).map((t) => (
                    <option key={t.slug} value={t.slug}>{t.label} · alavancagem 1:{t.leverage}</option>
                  ))}
                </select>
                {user?.role === "admin" ? (
                <>
                <span className={labelSmall}>Alavancagem extra (opcional)</span>
                <select data-testid="extra-leverage-select" value={extraLev} onChange={(e) => setExtraLev(e.target.value)}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  <option value="">Usar a da categoria</option>
                  {(profile?.extra_leverages || []).map((l) => (
                    <option key={l} value={l}>1:{l}</option>
                  ))}
                </select>
                </>
                ) : null}
                <Button type="submit" data-testid="submit-tier"
                        className="w-full rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
                  Aplicar categoria
                </Button>
              </form>
            ) : null}

            <form onSubmit={submitPassword} className={`${card} space-y-3`} data-testid="reset-password-form">
              <p className="flex items-center gap-2 text-sm text-white"><KeyRound className="h-4 w-4" /> Alterar palavra-passe</p>
              <Input required minLength={6} type="text" data-testid="new-password-input" value={password}
                     onChange={(e) => setPassword(e.target.value)} placeholder="Nova palavra-passe"
                     className="h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white" />
              <Button type="submit" data-testid="submit-new-password"
                      className="w-full rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
                Guardar
              </Button>
            </form>

            <ClientStatusHistory clientId={id} refreshKey={crmVersion} />

            <div className={card} data-testid="login-history">
              <p className="mb-2 text-sm text-white">Últimos acessos</p>
              {history.slice.map((h, i) => (
                <div key={`${h.created_at}-${i}`} className="border-b border-[#1A1D25] py-2 text-xs last:border-0">
                  <p className="font-mono text-[#94A3B8]">{fmtDate(h.created_at)}</p>
                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="font-mono text-white">{h.ip}</span>
                    <span className="uppercase text-[#60A5FA]">{h.method === "phone" ? "telefone" : "email"}</span>
                  </div>
                </div>
              ))}
              {history.total === 0 ? (
                <p className="py-4 text-center text-sm text-[#64748B]">Sem registos de acesso</p>
              ) : (
                <LocalPager page={history.page} pages={history.pages} total={history.total}
                            onPage={history.setPage} testId="login-history-pager" />
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
